<template>
  <div class="md-result-summary" :class="[statusClass]">
    <div class="summary-header" :style="headerStyle">
      <i :class="statusIcon" class="summary-icon"></i>
      {{ payload.title }}
    </div>
    <div class="summary-body">
      <div v-for="(item, idx) in payload.items" :key="idx" class="summary-row">
        <div class="summary-label">{{ item.label || $t('workbench.fieldN', { n: idx + 1 }) }}</div>
        <div class="summary-value">
          <a v-if="getHref(item)" :href="getHref(item)" target="_blank" rel="noopener noreferrer" class="summary-link">
            {{ item.value || getHref(item) }}
          </a>
          <span v-else class="summary-text">{{ item.value }}</span>
        </div>
      </div>
    </div>
    <div v-if="payload.buttons && payload.buttons.length" class="summary-buttons">
      <button
        v-for="(btn, idx) in payload.buttons"
        :key="idx"
        class="summary-btn"
        :class="btn.type === 'download' ? 'is-download' : btn.type === 'switchTab' ? 'is-switch' : 'is-silent'"
        @click="handleButton(btn)"
      >
        <i v-if="btn.type === 'switchTab'" class="el-icon-sort"></i>
        <i v-else-if="btn.type === 'silentMessage'" class="el-icon-chat-dot-round"></i>
        <i v-else-if="btn.type === 'download'" class="el-icon-download"></i>
        {{ btn.label }}
      </button>
    </div>
  </div>
</template>

<script>
const STATUS_MAP = {
  success: { color: '#67c23a', bg: 'rgba(103, 194, 58, 0.08)', icon: 'el-icon-circle-check' },
  info: { color: '#909399', bg: 'rgba(144, 147, 153, 0.08)', icon: 'el-icon-info' },
  error: { color: '#f56c6c', bg: 'rgba(245, 108, 108, 0.08)', icon: 'el-icon-circle-close' },
  warning: { color: '#e6a23c', bg: 'rgba(230, 162, 60, 0.08)', icon: 'el-icon-warning' },
  primary: { color: '#409eff', bg: 'rgba(64, 158, 255, 0.08)', icon: 'el-icon-star-on' }
}

export default {
  name: 'MdResultSummary',
  props: {
    title: { type: String, default: '' },
    items: { type: String, default: '' },
    data: { type: String, default: '' },
    status: { type: String, default: 'primary' },
    buttons: { type: [String, Array], default: '' },
    _rawJson: { type: String, default: '' },
    _componentKey: { type: String, default: '' }
  },
  computed: {
    payload() {
      const raw = this._parseJson(this._rawJson) || {}
      const rawItems = this._parseJson(this.items) ||
        this._parseJson(this.data) ||
        this._parseJson(raw.items) ||
        this._parseJson(raw.summary) ||
        []
      const status = this.status || raw.status || 'primary'
      const rawButtons = (Array.isArray(this.buttons) ? this.buttons : this._parseJson(this.buttons)) ||
        (Array.isArray(raw.buttons) ? raw.buttons : this._parseJson(raw.buttons)) ||
        []
      return {
        title: this.title || raw.title || this.$t('workbench.resultSummary'),
        items: Array.isArray(rawItems) ? rawItems : [],
        status,
        buttons: Array.isArray(rawButtons) ? rawButtons : []
      }
    },
    statusClass() {
      const s = this.payload.status
      return STATUS_MAP[s] ? `is-${s}` : 'is-primary'
    },
    statusIcon() {
      const s = this.payload.status
      return (STATUS_MAP[s] || STATUS_MAP.primary).icon
    },
    headerStyle() {
      const s = this.payload.status
      const cfg = STATUS_MAP[s] || STATUS_MAP.primary
      return {
        backgroundColor: cfg.bg,
        borderBottomColor: cfg.color,
        color: cfg.color
      }
    }
  },
  methods: {
    _parseJson(value) {
      if (!value) return null
      if (typeof value === 'object') return value
      if (typeof value !== 'string') return null
      const cleaned = value.replace(/\n/g, '')
      try {
        return JSON.parse(cleaned)
      } catch {
        try {
          const normalized = cleaned.replace(/'/g, '"').replace(/,\s*([}\]])/g, '$1')
          return JSON.parse(normalized)
        } catch {
          return null
        }
      }
    },
    getHref(item) {
      const href = item.href
      if (href) return href
      const val = item.value || ''
      if (/^https?:\/\//i.test(val)) return val
      return null
    },
    handleButton(btn) {
      if (!btn || !btn.type) return
      if (btn.type === 'switchTab') {
        let idx
        if (btn.index !== undefined && btn.index !== null) {
          idx = typeof btn.index === 'number' ? btn.index : parseInt(btn.index, 10)
        }
        if (isNaN(idx)) {
          const items = this.$store.state.cosmoClaw.iframeItems || []
          const current = this.$store.state.cosmoClaw.iframeActiveIndex || 0
          idx = items.length > 1 ? (current + 1) % items.length : 0
        }
        this.$store.commit('cosmoClaw/SET_IFRAME_ACTIVE_INDEX', idx)
      } else if (btn.type === 'silentMessage') {
        const content = btn.content || btn.message || ''
        if (content) {
          this.$store.dispatch('cosmoClaw/sendSilentMessage', { content })
        }
      } else if (btn.type === 'download') {
        const url = btn.url || btn.href || ''
        if (url) {
          const a = document.createElement('a')
          a.href = url
          a.download = btn.filename || ''
          a.target = '_blank'
          a.rel = 'noopener noreferrer'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.md-result-summary {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  margin: 12px 0;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
}

.summary-header {
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.summary-icon {
  font-size: 18px;
}

.summary-body {
  padding: 0 16px;
}

.summary-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
}

.summary-label {
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
}

.summary-value {
  font-size: 13px;
  line-height: 1.6;
  color: #111827;
  min-width: 0;
  word-break: break-all;
}

.summary-link {
  color: #2563eb;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.summary-text {
  color: #111827;
}

.summary-buttons {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #f3f4f6;
  justify-content: flex-end;
}

.summary-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;
  line-height: 1.4;

  i {
    font-size: 14px;
  }

  &.is-switch {
    background: #eff6ff;
    color: #2563eb;
    border-color: #bfdbfe;

    &:hover {
      background: #dbeafe;
    }
  }

  &.is-silent {
    background: #f0fdf4;
    color: #16a34a;
    border-color: #bbf7d0;

    &:hover {
      background: #dcfce7;
    }
  }

  &.is-download {
    background: #fefce8;
    color: #ca8a04;
    border-color: #fde68a;

    &:hover {
      background: #fef9c3;
    }
  }
}
</style>
